'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import StravaAuthButton from './StravaAuthButton';
import EmailLoginForm from './EmailLoginForm';
import EmailSignupForm from './EmailSignupForm';
import { Toaster } from 'sonner';

type AuthMode = 'login' | 'signup';
type AuthMethod = 'strava' | 'email';

const CLUB_CLIMBS = [
  'Passo dello Stelvio', 'Passo del Mortirolo', 'Passo di Gavia',
  'Passo Giau', 'Monte Zoncolan', 'Colle delle Finestre',
];

export default function AuthPage() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [method, setMethod] = useState<AuthMethod>('strava');

  return (
    <div className="min-h-screen bg-background flex">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] xl:w-[40%] bg-card border-r border-border p-10 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full opacity-8"
            style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={40} />
            <span className="font-sans font-extrabold text-xl tracking-tight">
              <span className="text-gradient-orange">IA</span>
              <span className="text-foreground">ClimbBoard</span>
            </span>
          </Link>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-extrabold text-foreground leading-tight mb-4">
            Scala le classifiche.<br />
            <span className="text-gradient-orange">Batti il tuo record.</span>
          </h1>
          <p className="text-base text-muted-foreground mb-8 leading-relaxed">
            Unisciti al club IA Climb e competi con i migliori ciclisti italiani sulle 9 salite iconiche.
            Tempi verificati direttamente da Strava.
          </p>

          <div className="space-y-3">
            {CLUB_CLIMBS.map((name) => (
              <div key={`brand-climb-${name}`} className="flex items-center gap-3">
                <Icon name="ArrowTrendingUpIcon" size={16} className="text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{name}</span>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <Icon name="EllipsisHorizontalIcon" size={16} className="text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-muted-foreground">e altre 3 salite...</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-muted-foreground">12 membri attivi</span>
          </div>
          <span className="text-border">|</span>
          <span className="text-xs text-muted-foreground">45 tempi verificati</span>
          <span className="text-border">|</span>
          <span className="text-xs text-muted-foreground">Stagione 2026</span>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <AppLogo size={36} />
            <span className="font-sans font-extrabold text-lg tracking-tight">
              <span className="text-gradient-orange">IA</span>
              <span className="text-foreground">ClimbBoard</span>
            </span>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center bg-muted rounded-xl p-1 mb-6">
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                  mode === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Accedi' : 'Registrati'}
              </button>
            ))}
          </div>

          <div className="fade-in" key={`auth-mode-${mode}`}>
            <h2 className="text-xl font-bold text-foreground mb-1">
              {mode === 'login' ? 'Bentornato!' : 'Unisciti al club'}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              {mode === 'login' ?'Accedi per vedere la tua posizione in classifica.' :'Crea il tuo account e inizia a competere sulle salite.'}
            </p>

            {/* Method toggle */}
            <div className="flex items-center gap-2 mb-5">
              {(['strava', 'email'] as AuthMethod[]).map((m) => (
                <button
                  key={`method-${m}`}
                  onClick={() => setMethod(m)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all duration-150 ${
                    method === m
                      ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {m === 'strava' ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                      </svg>
                      Strava
                    </>
                  ) : (
                    <>
                      <Icon name="EnvelopeIcon" size={14} />
                      Email
                    </>
                  )}
                </button>
              ))}
            </div>

            {method === 'strava' ? (
              <div>
                <StravaAuthButton mode={mode} />
                <div className="mt-4 p-4 bg-orange-500/5 border border-orange-500/20 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Icon name="InformationCircleIcon" size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-orange-400 mb-1">Requisito club</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Devi essere membro del club Strava &quot;IA Climb&quot; per accedere alla classifica.
                        {mode === 'signup' && ' Il tuo account verrà verificato dall\'admin prima dell\'attivazione.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {mode === 'login' ? <EmailLoginForm /> : <EmailSignupForm />}
                {mode === 'signup' && (
                  <div className="mt-4 p-4 bg-yellow-400/5 border border-yellow-400/20 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Icon name="ExclamationTriangleIcon" size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-yellow-400 mb-1">Verifica manuale</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          La registrazione via email richiede verifica manuale dell&apos;admin. Assicurati di essere iscritto al club Strava &quot;IA Climb&quot; per velocizzare il processo.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {mode === 'login' ? 'Non hai un account?' : 'Hai già un account?'}
                {' '}
                <button
                  onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                  className="text-primary hover:underline font-semibold"
                >
                  {mode === 'login' ? 'Registrati' : 'Accedi'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}